import { Spacing } from '@persona-id/design-tokens';
import { partition, startCase } from 'lodash/fp';
import { ChangeEvent } from 'react';
import styled from 'styled-components';

import { MATCH_REQUIREMENT_CHECK_NAMES } from 'dashboard/apps/InquiryTemplateEditor/InquiryPreview/utils';
import ReactSelect from 'dashboard/components/ReactSelect';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { CheckStub, MatchRequirementsCheckStub } from 'dashboard/models/simulate/stubs';
import { checkLifecycleStage } from 'dashboard/models/verification-check';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import Checkbox from 'design-system/components/Checkbox/Checkbox';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { CHECK_DISPLAY_CONFIG, CHECK_REASON_MAPPING } from 'lib/constants/check';
import { VerificationStatus } from 'lib/verification';

const FAILURE_REASON_SELECT_PREFIX = 'failure_reason_select';

const CheckNameContainer = styled.div`
  display: flex;
  gap: ${Spacing[2]};
  align-items: center;
`;

const getCheckPrettyName = (checkConfig: VerificationCheckConfig) => {
  return CHECK_DISPLAY_CONFIG[checkConfig.check]?.name || startCase(checkConfig.check);
};

interface VerificationCheckStubsPanel {
  verificationTemplateVersion: VerificationTemplateVersion;
  checkStubs: (CheckStub | MatchRequirementsCheckStub)[];
  setCheckStubs: (checkStubs: (CheckStub | MatchRequirementsCheckStub)[]) => void;
  onOutsideClick: () => void;
  offsetElementId?: string;
  trackEvent: (eventName: string, props?: Record<string, any>) => void;
}

export const CheckStubsPanel = ({
  verificationTemplateVersion,
  checkStubs,
  setCheckStubs,
  onOutsideClick,
  offsetElementId,
  trackEvent,
}: VerificationCheckStubsPanel) => {
  const verificationCheckConfigs = verificationTemplateVersion.attributes['verification-checks']
    .filter((checkConfig) => !MATCH_REQUIREMENT_CHECK_NAMES.includes(checkConfig.check))
    .sort((a, b) => (getCheckPrettyName(a) < getCheckPrettyName(b) ? -1 : 1));

  const [requiredCheckConfigs, nonRequiredCheckConfigs] = partition(
    (config) => config['on-failed'] === VerificationStatus.Failed,
    verificationCheckConfigs,
  );

  const [nonRequiredEnabledCheckConfigs, nonRequiredDisabledCheckConfigs] = partition(
    (config) => config['toggle-option'] === 'enabled',
    nonRequiredCheckConfigs,
  );

  const verificationCheckConfigsSorted = [
    ...requiredCheckConfigs,
    ...nonRequiredEnabledCheckConfigs,
    ...nonRequiredDisabledCheckConfigs,
  ];

  return (
    <MaximizablePanel
      title='Verification Checks'
      subtitle='Simulate verification outcomes by selecting verification checks to fail.'
      onOutsideClickAndEscPress={onOutsideClick}
      offsetElementId={offsetElementId}
      outsideExceptionalIdPrefixes={[FAILURE_REASON_SELECT_PREFIX]}
    >
      <Table>
        <TableHead sticky>
          <TableHeaderCell width='min'>Fail</TableHeaderCell>
          <TableHeaderCell width='min'>Verification check</TableHeaderCell>
          <TableHeaderCell width='min'>Required</TableHeaderCell>
          <TableHeaderCell width='max'>Failure reason</TableHeaderCell>
        </TableHead>
        <TableBody>
          <>
            {verificationCheckConfigsSorted.map((checkConfig, idx) => {
              const checkStubIndex = checkStubs.findIndex(
                (checkStub) => checkStub.name === checkConfig.check,
              );
              const checkStub =
                checkStubIndex > -1 ? (checkStubs[checkStubIndex] as CheckStub) : null;

              return (
                <TableRow key={idx}>
                  <TableCell>
                    <Checkbox
                      checked={checkStub?.status === 'failed'}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                          trackEvent('Flow Editor - Preview Check Selected', {
                            check_name: checkConfig.check,
                          });
                          setCheckStubs([
                            ...checkStubs,
                            {
                              name: checkConfig.check,
                              status: 'failed',
                              reasons: (
                                CHECK_REASON_MAPPING[checkConfig.check]?.Failed ?? []
                              ).slice(0, 1),
                            },
                          ]);
                        } else {
                          const newCheckStubs = checkStubs.slice();
                          newCheckStubs.splice(checkStubIndex, 1);
                          setCheckStubs(newCheckStubs);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <CheckNameContainer>
                      {CHECK_DISPLAY_CONFIG[checkConfig.check]?.name ||
                        startCase(checkConfig.check)}
                      {checkConfig['toggle-option'] === 'disabled' && (
                        <Sticker variant='caution'>Disabled</Sticker>
                      )}
                      {checkLifecycleStage(checkConfig.check) === 'sunset' && (
                        <Sticker variant='caution'>Sunset</Sticker>
                      )}
                      {checkLifecycleStage(checkConfig.check) === 'deprecated' && (
                        <Sticker variant='caution'>Deprecated</Sticker>
                      )}
                    </CheckNameContainer>
                  </TableCell>
                  <TableCell>
                    {checkConfig['on-failed'] === VerificationStatus.Failed ? 'Yes' : 'No'}
                  </TableCell>
                  <TableCell>
                    <ReactSelect
                      id={`${FAILURE_REASON_SELECT_PREFIX}_${idx}`}
                      value={checkStub && checkStub.reasons?.[0]}
                      options={(CHECK_REASON_MAPPING[checkConfig.check]?.Failed ?? []).map(
                        (option) => ({
                          value: option,
                          // Attempt to map to the long-form description; otherwise fallback
                          // to the programmatic check name
                          label:
                            CHECK_DISPLAY_CONFIG[checkConfig.check]?.reasons?.[option] || option,
                        }),
                      )}
                      disabled={checkStub == null}
                      onChange={(value: string) => {
                        if (checkStubIndex === -1 || checkStub == null) {
                          return;
                        }
                        trackEvent('Flow Editor - Preview Failure Reason Selected', {
                          failure_reason: value,
                        });

                        const newCheckStubs = checkStubs.slice();
                        newCheckStubs.splice(checkStubIndex, 1, {
                          ...checkStub,
                          reasons: [value],
                        });

                        setCheckStubs(newCheckStubs);
                      }}
                      portal
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </>
        </TableBody>
      </Table>
    </MaximizablePanel>
  );
};
